import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon, WrenchIcon, PuzzleIcon, BookOpenIcon, CheckCircle2Icon, XCircleIcon, Loader2Icon, UserIcon, BotIcon } from 'lucide-react';
import { ChatMessage, SkillCall, ToolCall, KnowledgeRetrievalCall } from '../../types';
import { Markdown } from '../common/Markdown';
export function ToolCallCard({
  call
}: {
  call: ToolCall;
}) {
  const [open, setOpen] = useState(false);
  return <div className="my-2 overflow-hidden rounded-lg border border-slate-700 bg-slate-800/40" data-id="element-111">
      <button onClick={() => setOpen(o => !o)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-800/60" data-id="element-112">
        <ChevronRightIcon className={`h-4 w-4 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} data-id="element-113" />
        <WrenchIcon className="h-4 w-4 text-amber-400" data-id="element-114" />
        <span className="font-mono text-xs text-slate-300" data-id="element-115">{call.name}</span>
        <span className="ml-auto flex items-center gap-1" data-id="element-116">
          {call.status === 'running' && <Loader2Icon className="h-3.5 w-3.5 animate-spin text-amber-400" data-id="element-117" />}
          {call.status === 'success' && <CheckCircle2Icon className="h-3.5 w-3.5 text-emerald-400" data-id="element-118" />}
          {call.status === 'failed' && <XCircleIcon className="h-3.5 w-3.5 text-red-400" data-id="element-119" />}
          {call.durationMs != null && <span className="text-xs text-slate-500" data-id="element-120">{call.durationMs}ms</span>}
        </span>
      </button>
      {open && <div className="space-y-2 border-t border-slate-700 px-3 py-2" data-id="element-121">
          <div data-id="element-122">
            <div className="text-xs text-slate-500" data-id="element-123">输入</div>
            <pre className="mt-0.5 overflow-x-auto rounded bg-slate-950 p-2 font-mono text-xs text-slate-300" data-id="element-124">
              {call.input}
            </pre>
          </div>
          {call.output && <div data-id="element-125">
              <div className="text-xs text-slate-500" data-id="element-126">输出</div>
              <pre className="mt-0.5 overflow-x-auto rounded bg-slate-950 p-2 font-mono text-xs text-slate-300" data-id="element-127">
                {call.output}
              </pre>
            </div>}
        </div>}
    </div>;
}
export function SkillCallCard({
  call
}: {
  call: SkillCall;
}) {
  return <div className="my-2 rounded-lg border border-purple-500/30 bg-purple-500/5 px-3 py-2" data-id="element-128">
      <div className="flex items-center gap-2 text-sm" data-id="element-129">
        <PuzzleIcon className="h-4 w-4 text-purple-400" data-id="element-130" />
        <span className="font-medium text-slate-200" data-id="element-131">{call.skillName}</span>
        <span className="font-mono text-xs text-slate-500" data-id="element-132">
          v{call.version}
        </span>
        <span className="ml-auto flex items-center gap-1" data-id="element-133">
          {call.status === 'running' && <Loader2Icon className="h-3.5 w-3.5 animate-spin text-purple-400" data-id="element-134" />}
          {call.status === 'success' && <CheckCircle2Icon className="h-3.5 w-3.5 text-emerald-400" data-id="element-135" />}
          {call.status === 'failed' && <XCircleIcon className="h-3.5 w-3.5 text-red-400" data-id="element-136" />}
          {call.durationMs != null && <span className="text-xs text-slate-500" data-id="element-137">{call.durationMs}ms</span>}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-400" data-id="element-138">
        <span data-id="element-139">输入：{call.inputSummary}</span>
        {call.outputSummary && <span data-id="element-140">输出：{call.outputSummary}</span>}
      </div>
    </div>;
}
export function KnowledgeRetrievalCard({
  call
}: {
  call: KnowledgeRetrievalCall;
}) {
  const [open, setOpen] = useState(false);
  return <div className="my-2 overflow-hidden rounded-lg border border-emerald-500/30 bg-emerald-500/5" data-id="element-141">
      <button onClick={() => setOpen(o => !o)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-emerald-500/10" data-id="element-142">
        <ChevronRightIcon className={`h-4 w-4 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} data-id="element-143" />
        <BookOpenIcon className="h-4 w-4 text-emerald-400" data-id="element-144" />
        <span className="font-medium text-slate-200" data-id="element-145">知识库检索</span>
        <span className="truncate text-xs text-slate-500" data-id="element-146">
          {call.knowledgeBaseName}
        </span>
        <span className="ml-auto flex items-center gap-1" data-id="element-147">
          {call.status === 'running' && <Loader2Icon className="h-3.5 w-3.5 animate-spin text-emerald-400" data-id="element-148" />}
          {call.status === 'success' && <CheckCircle2Icon className="h-3.5 w-3.5 text-emerald-400" data-id="element-149" />}
          {call.status === 'failed' && <XCircleIcon className="h-3.5 w-3.5 text-red-400" data-id="element-150" />}
          {call.durationMs != null && <span className="text-xs text-slate-500" data-id="element-151">{call.durationMs}ms</span>}
        </span>
      </button>
      <div className="px-3 pb-2 text-xs text-slate-400" data-id="element-152">查询：{call.query}</div>
      {open && call.hits && call.hits.length > 0 && <div className="space-y-1.5 border-t border-emerald-500/20 px-3 py-2" data-id="element-153">
          {call.hits.map((h, i) => <div key={i} className="rounded-md bg-slate-950/50 p-2" data-id="element-154">
              <div className="mb-0.5 flex items-center justify-between text-xs" data-id="element-155">
                <span className="font-medium text-emerald-300" data-id="element-156">
                  [{i + 1}] {h.documentName}
                </span>
                <span className="font-mono text-slate-500" data-id="element-157">
                  score {h.score}
                </span>
              </div>
              <p className="text-xs leading-relaxed text-slate-400" data-id="element-158">
                {h.snippet}
              </p>
            </div>)}
        </div>}
    </div>;
}
export function MessageBubble({
  message
}: {
  message: ChatMessage;
}) {
  if (message.role === 'system') {
    return <div className="my-2 text-center" data-id="element-159">
        <span className="rounded-full bg-slate-800 px-3 py-1 text-xs text-slate-400" data-id="element-160">
          {message.content}
        </span>
      </div>;
  }
  const isUser = message.role === 'user';
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-161">
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isUser ? 'bg-indigo-600' : 'bg-slate-700'}`} data-id="element-162">
        {isUser ? <UserIcon className="h-4 w-4 text-white" data-id="element-163" /> : <BotIcon className="h-4 w-4 text-indigo-300" data-id="element-164" />}
      </div>
      <div className={`min-w-0 max-w-[85%] ${isUser ? 'items-end' : 'items-start'}`} data-id="element-165">
        <div className={`rounded-2xl px-4 py-2.5 ${isUser ? 'bg-indigo-600 text-white' : 'bg-slate-800/70 text-slate-200'}`} data-id="element-166">
          {isUser ? <p className="whitespace-pre-wrap text-sm" data-id="element-167">{message.content}</p> : <>
              {message.knowledgeCalls?.map(c => <KnowledgeRetrievalCard key={c.id} call={c} data-id="element-168" />)}
              {message.skillCalls?.map(c => <SkillCallCard key={c.id} call={c} data-id="element-169" />)}
              {message.toolCalls?.map(c => <ToolCallCard key={c.id} call={c} data-id="element-170" />)}
              {message.content ? <Markdown content={message.content} data-id="element-171" /> : message.streaming && <div className="flex items-center gap-1.5 py-1 text-sm text-slate-400" data-id="element-172">
                    <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-173" /> 思考中…
                  </div>}
              {message.streaming && message.content && <span className="cursor-blink ml-0.5 inline-block h-4 w-[2px] translate-y-0.5 bg-indigo-400" data-id="element-174" />}
            </>}
        </div>
      </div>
    </motion.div>;
}